import styled, { css, DefaultTheme } from 'styled-components';

import { Input } from '@components';
import { TransientProps } from '@common/types';
import { RoomAskingStatus } from '@common/types/api';

import { VirtualizedItemWrapper } from '../styles';

type ContainerProps = TransientProps<{ disabled: boolean; hide: boolean }>;

export const Container = styled.form<ContainerProps>`
  ${({ $disabled: isDisabled, $hide: shouldHide }) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    padding: 0.5rem 0 1rem;
    transition: 500ms ease-in-out;

    ${isDisabled &&
    css`
      opacity: 0;
      pointer-events: none;
      user-select: none;
    `}

    ${shouldHide &&
    css`
      display: none;
    `}
  `}
`;

export const Wrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    max-height: 90%;

    h3 {
      font-size: 1rem;
      font-weight: 500;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid ${theme.colors.background[60]};
    }
  `}
`;

export const QuestionsListWrapper = styled.div`
  margin-bottom: 1rem;
  height: 100%;
  max-height: calc(100% - 3.5rem);
  overflow-y: auto;
`;

type QuestionsListProps = { height: number };

export const QuestionsList = styled.ul<QuestionsListProps>`
  ${({ height }) => css`
    width: 100%;
    height: ${height}px;
    padding-top: 1rem;
    position: relative;
  `}
`;

const statusBadgeStyles = ({ colors }: DefaultTheme) => ({
  0: { bg: '#FFFBEB', color: '#F59E0B' },
  1: { bg: '#ECFDF5', color: colors.success[100] },
  2: { bg: colors.brand.primary.opaque, color: colors.brand.primary.default },
});

export const QuestionWrapper = styled(VirtualizedItemWrapper)`
  padding: 0.5rem 0.5rem 0;
`;

type QuestionProps = TransientProps<{ status: RoomAskingStatus }>;

export const Question = styled.div<QuestionProps>`
  ${({ theme }) => css`
    display: flex;
    gap: 0;
    border: 1px solid ${theme.colors.background[40]};
    border-radius: ${theme.radii.md};
    overflow: hidden;
    width: 100%;
    background: ${theme.colors.background[60]};
    transition: border-color 0.15s;
  `}
`;

type IconColProps = TransientProps<{ status: RoomAskingStatus }>;

const iconColors = ({ colors }: DefaultTheme) => ({
  0: '#F59E0B',
  1: colors.success[100],
  2: colors.brand.primary.default,
});

export const QuestionIconCol = styled.div<IconColProps>`
  ${({ theme, $status: status }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem 0.625rem;
    min-width: 2.625rem;

    svg {
      width: 1.125rem;
      height: 1.125rem;
      color: ${iconColors(theme)[status]};
    }
  `}
`;

export const QuestionContent = styled.div`
  flex: 1;
  padding: 0.625rem 0.75rem;
  min-width: 0;
`;

export const QuestionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.25rem;
`;

export const QuestionAuthor = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  color: #1b2a4a;
`;

export const QuestionTime = styled.span`
  font-size: 0.625rem;
  color: #94a3b8;
`;

type StatusBadgeProps = TransientProps<{ status: RoomAskingStatus }>;

export const StatusBadge = styled.span<StatusBadgeProps>`
  ${({ theme, $status: status }) => {
    const styles = statusBadgeStyles(theme)[status];
    return css`
      font-size: 0.53125rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      padding: 0.0625rem 0.375rem;
      border-radius: 0.1875rem;
      margin-left: auto;
      background: ${styles.bg};
      color: ${styles.color};
    `;
  }}
`;

export const QuestionText = styled.div`
  font-size: 0.78125rem;
  font-weight: 500;
  color: #3d4f6f;
  line-height: 1.4;
  margin-bottom: 0.375rem;
`;

export const AnswerBox = styled.div`
  font-size: 0.71875rem;
  color: #3d4f6f;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 0.375rem;
`;

export const AnswerLabel = styled.span`
  ${({ theme }) => css`
    font-size: 0.625rem;
    font-weight: 700;
    color: ${theme.colors.success[100]};
    flex-shrink: 0;
    margin-top: 0.0625rem;
  `}
`;

export const QuestionInput = styled(Input)`
  height: 3.25rem;
  max-width: calc(100% - 2rem);
  margin: 0 1rem;

  > svg {
    margin: 0 0 0 0.5rem;
  }

  input {
    font-size: 0.875rem;
  }
`;
